import type { Player } from '@mfc/mfc-video-sdk'
import { loadWithRetry } from '../utilities/dynamicWithRetry'

export type VideoPlayerClass = typeof Player

export type AcrDecision = {
    /** The visitor is age-gated: every player runs in the SDK's ACR mode */
    acr: boolean
    /** ACR blocks the live feed's audio (www and mobile: unless with_video) */
    noAudio: boolean
}

let playerClass: VideoPlayerClass | null = null
let pending: Promise<VideoPlayerClass> | null = null

let settleAcrDecision: (decision: AcrDecision) => void = () => {}
const acrDecision = new Promise<AcrDecision>((resolve) => {
    settleAcrDecision = resolve
})
let decided: AcrDecision | null = null

/**
 * Records whether the visitor is age-gated; getVisitorInformation calls it
 * once the visitor lookup has answered, or failed. Only the first call
 * counts. The SDK's ACR mode is global to the page and set once
 * (Player.setACR ignores every later call, and nothing undoes it), so it
 * has to be on before the first Player exists: videoPlayerReady holds the
 * class back until this has run, which covers grid previews built before
 * any room opens.
 */
export const decideACR = (decision: AcrDecision) => {
    decided ??= decision
    settleAcrDecision(decision)
}

/**
 * Whether the SDK's ACR mode keeps the live feed's controls off (no_audio).
 * It then shows them, volume only, just while a clip plays, turning them on
 * and off itself as clips start and end. Player.setAllowControls takes no
 * notice of ACR: turned on over the live feed, it brings back the full bar.
 */
export const acrHidesLiveControls = () => !!decided?.acr && decided.noAudio

export const getVideoPlayerClass = () => playerClass

export const isVideoPlayerReady = () => playerClass !== null

/**
 * Resolves with the SDK `Player` class once the `@mfc/mfc-video-sdk` chunk
 * has loaded and decideACR has run. The SDK is dynamically imported so the
 * (large) player bundle stays out of the main chunk; await this before
 * constructing a player.
 *
 * One load runs at a time and goes through the app's standard chunk retry
 * (`loadWithRetry`, a few quick attempts for bad connections). If the round
 * still fails the promise rejects and the next call starts a fresh round, so
 * callers that ask again on later user activity recover without a reload.
 */
export const videoPlayerReady = (): Promise<VideoPlayerClass> => {
    if (playerClass) {
        return Promise.resolve(playerClass)
    }

    if (pending === null) {
        pending = loadWithRetry(() => import('@mfc/mfc-video-sdk'), {
            // this chunk is far larger than a route chunk; give a flaky
            // connection a little longer between attempts
            delay: 1000,
        })
            .then(async ({ Player }) => {
                const { acr, noAudio } = await acrDecision
                if (acr) Player.setACR({ no_audio: noAudio })
                playerClass = Player
                return Player
            })
            .catch((error: unknown) => {
                pending = null
                throw error
            })
    }

    return pending
}
